import React from 'react';
import {
  Action, ActionProps, FormHandlers, Portal, useModal, useNotifications,
} from '@doveit/bricks';
import { HRInterview, JobApplication } from '../../../providers/api/dtos';
import { JobApplicationStatus, JobApplicationType } from '../../../domain/types';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import { createHRCalendarEvent, updateHRCalendarEvent } from '../../../providers/api/hr/hrProvider';
import { updateJobApplication } from '../../../providers/api/job-application/jobApplicationProvider';
import { HRInterviewFormModel } from '../../components/hr-interview-form/types';
import HRInterviewForm from '../../components/hr-interview-form/HRInterviewForm';
import { hrInterviewFormModelToHRInterview, hrInterviewToHRInterviewFormModel } from '../../components/hr-interview-form/mappers';
import { booleanStringToBool } from '../../../utils/boolean-string/booleanString';
import { useHRCalendarEvents } from '../../../hr/hooks/use-hr-calendar-events/useHRCalendarEvents';
import { HRInterviewStatus } from '../../../hr/constants';

export const HR_INTERVIEW_CREATE_SUCCESS_MESSAGE = 'Il colloquio è stato fissato con successo.';
export const HR_INTERVIEW_CREATE_ERROR_MESSAGE = 'Non è stato possibile fissare il colloquio.';
export const HR_INTERVIEW_UPDATE_SUCCESS_MESSAGE = 'Il colloquio è stato aggiornato con successo.';
export const HR_INTERVIEW_UPDATE_ERROR_MESSAGE = 'Non è stato possibile aggiornare il colloquio.';

interface ChildFnProps {
  openModal: VoidFunction,
  isSaving: boolean,
}

interface BaseProps extends React.AriaAttributes {
  jobApplication: JobApplication,
  onSuccess?: (interview: HRInterview) => void,
  children?: (childFnProps: ChildFnProps) => React.ReactNode,
  size?: ActionProps['size'],
}

export interface CreateHRInterviewActionProps extends BaseProps {
  hrInterview?: HRInterview & { id?: undefined },
  canCreate: boolean,
  canUpdate?: never,
}

export interface UpdateHRInterviewActionProps extends BaseProps {
  hrInterview: HRInterview,
  canUpdate: boolean,
  canCreate?: never,
}

export type UpsertHRInterviewActionProps = CreateHRInterviewActionProps | UpdateHRInterviewActionProps;

const UpsertHRInterviewAction: React.FC<UpsertHRInterviewActionProps> = ({
  hrInterview,
  jobApplication,
  size = 'S',
  canCreate = false,
  canUpdate = false,
  children,
  onSuccess,
  ...rest
}) => {
  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;
  const [isSaving, setIsSaving] = React.useState(false);
  const { user, userIsHR } = useRBAC();
  const modal = useModal();
  const { addSuccess, addError } = useNotifications();
  const isCreation = !hrInterview?.id;
  const canDoAction = isCreation ? canCreate : canUpdate;

  const { data: calendarEvents } = useHRCalendarEvents({ jobApplicationId: jobApplication.id, isActive: true });
  const anActiveInterviewAlredyExists = React.useMemo(() => calendarEvents?.content.some((it) => it.type === 'INTERVIEW'), [calendarEvents?.content]);

  const handleSubmit = React.useCallback(async (values: HRInterviewFormModel) => {
    setIsSaving(true);

    try {
      let upsertedInterview: HRInterview;
      const payload: HRInterview = hrInterviewFormModelToHRInterview(values, jobApplication, hrInterview);

      if (!jobApplication.supervisorEmail || !jobApplication.assigneeEmail || [JobApplicationStatus.IN_PROGRESS, JobApplicationStatus.ASSIGNED].includes(jobApplication.status)) {
        const jobApplicationToUpdate: JobApplication = {
          ...jobApplication,
          status: [JobApplicationStatus.IN_PROGRESS, JobApplicationStatus.ASSIGNED].includes(jobApplication.status) ? JobApplicationStatus.INTERVIEW_SCHEDULED : jobApplication.status,
          supervisorEmail: jobApplication.supervisorEmail || values.supervisorEmail,
          assigneeEmail: jobApplication.assigneeEmail || values.assigneeEmail,
          ...(jobApplication.applicationType === JobApplicationType.SALES && { isFranchising: booleanStringToBool(values.isFranchising) }),
        };

        await updateJobApplication(jobApplicationToUpdate.id!, jobApplicationToUpdate);
      }

      if (isCreation) {
        upsertedInterview = await createHRCalendarEvent(payload);
      } else {
        upsertedInterview = await updateHRCalendarEvent(
          hrInterview.id!,
          payload,
        );
      }

      setIsSaving(false);
      addSuccess(isCreation ? HR_INTERVIEW_CREATE_SUCCESS_MESSAGE : HR_INTERVIEW_UPDATE_SUCCESS_MESSAGE);
      modal.close();

      onSuccess?.(upsertedInterview);
    } catch {
      setIsSaving(false);
      addError(isCreation ? HR_INTERVIEW_CREATE_ERROR_MESSAGE : HR_INTERVIEW_UPDATE_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, hrInterview, isCreation, jobApplication, modal, onSuccess]);

  if (!canDoAction
    // We want to prevent the creation of a new interview if another one is active
    || (isCreation && anActiveInterviewAlredyExists)
    // We wanto to prevent the editing of an interview in a final status
    || (!isCreation && hrInterview.status !== HRInterviewStatus.Todo)
  ) return null;

  return (
    <>
      {children
        ? children({
          isSaving,
          openModal: modal.open,
        }) : (
          <Action
            aria-label={`Azione per ${isCreation ? 'fissare' : 'modificare'} il colloquio`}
            label={`${isCreation ? 'Fissa' : 'Modifica'} il colloquio`}
            onClick={() => modal.open()}
            loading={isSaving}
            size={size}
            {...rest}
          />
        )}
      <Portal>
        <SimpleModal
          {...modal}
          title={`${isCreation ? 'Fissa' : 'Modifica'} il colloquio `}
          aria-label={`Modale per ${isCreation ? 'fissare' : 'modificare'} il colloquio `}
          footer={(
            <Action
              label={isCreation ? 'Fissa' : 'Modifica'}
              color="primary"
              emphasis="high"
              aria-label="Modale per salvare il colloquio"
              onClick={() => formRef.current.handleSubmit()}
              loading={isSaving}
            />
          )}
        >
          <HRInterviewForm
            applicationType={jobApplication.applicationType}
            initialValues={hrInterviewToHRInterviewFormModel(
              jobApplication,
              hrInterview,
              { supervisorEmail: userIsHR ? user.email : '' },
            )}
            hiddenFields={{
              supervisorEmail: !!jobApplication.supervisorEmail,
              assigneeEmail: !!jobApplication.assigneeEmail,
            }}
            innerRef={formRef}
            onSubmit={handleSubmit}
            loading={isSaving}
          />
        </SimpleModal>
      </Portal>
    </>
  );
};

export default UpsertHRInterviewAction;
